/**
 * One venue, as a phone card (BON-538).
 *
 * The roster's columns do not survive at 390px — the figures end up behind a
 * sideways scroll, which defeats the one thing the roster is for — so the same
 * row is restated as a card. Same facts, reordered by what is read first: the
 * name, then whose territory it is, then the three counts, then the fill.
 *
 * A component driven by a typed venue rather than markup inlined in the page,
 * so a later narrow-screen surface listing venues can lift it whole. It is NOT
 * promoted to `primitives/` yet — there is no second caller to shape it, and a
 * primitive with one user is a guess about the next one.
 */
import { Link } from 'react-router-dom';

import { asCount } from '../../../../utils/counts';
import { FillBar } from '../../primitives/FillBar';

import { OpenTasksChip } from './OpenTasksChip';
import { useRegionColor } from './region-colors-context';
import { cityLine, partnerSince } from './venue-columns';
import { VenueCardStat } from './VenueCardStat';
import type { VenueRosterRow } from './venues-roster-api';

export interface VenueCardProps {
  venue: VenueRosterRow;
  /** Where the card goes when tapped. Omit for a card that is not a link. */
  href?: string;
}

export function VenueCard({ venue, href }: VenueCardProps) {
  const color = useRegionColor()(venue.region);

  const card = (
    <>
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, lineHeight: 1.25 }}>{venue.name}</h3>
      <p style={{ margin: '4px 0 0', fontSize: 13.5, fontWeight: 600, color }}>
        {venue.region?.name ?? 'Unassigned'}
      </p>
      <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--nb-subtle)' }}>
        {cityLine(venue)} · partner since {partnerSince(venue.partnerSince)}
      </p>

      {asCount(venue.openActionItemCount) > 0 && (
        <div style={{ marginTop: 10 }}>
          <OpenTasksChip count={venue.openActionItemCount} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 22, marginTop: 12 }}>
        <VenueCardStat label="Total run" value={venue.totalRun} />
        <VenueCardStat label="Upcoming" value={venue.upcoming} />
        <VenueCardStat label="Capacity" value={venue.capacity} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <FillBar
            sold={venue.sold}
            cap={venue.capacity}
            width="100%"
            ariaLabel={`${venue.sold} of ${venue.capacity} seats sold`}
          />
        </div>
        <span
          className="nb-tabular"
          style={{ fontSize: 13, color: 'var(--nb-subtle)', whiteSpace: 'nowrap' }}
        >
          {venue.sold}/{venue.capacity} sold
        </span>
      </div>
    </>
  );

  // The colour stripe is the card's left edge, so the region reads before the
  // text does when scrolling a stack of them.
  const style: React.CSSProperties = {
    display: 'block',
    padding: '14px 16px',
    borderRadius: 10,
    border: '1px solid var(--nb-border)',
    borderLeft: `4px solid ${color}`,
    background: 'var(--nb-bg)',
    color: 'inherit',
    textDecoration: 'none',
  };

  return href ? (
    <Link to={href} style={style}>
      {card}
    </Link>
  ) : (
    <article style={style}>{card}</article>
  );
}
